import React from 'react';
import { Link, RichText } from '@/components';
import { Button } from '@/components/ui/button';
import type { PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { formattedDate } from '@/lib/utils';
import { InternalLinkBuilder } from '@/utils';

interface Slug {
  current: string;
}

interface PageParent {
  slug: Slug;
  pageParent?: PageParent;
}

interface DetailCardProps {
  result: {
    _id: string;
    _type: string;
    title: string;
    shortTitle?: string;
    slug?: Slug;
    pageParent?: PageParent;
    language: string;
    externalUrl?: string;
    blurb?: PortableTextBlockType;
    abstract?: string;
    content?: PortableTextBlockType;
    _createdAt?: string;
    publicationDate?: string;
    date?: string;
  };
}

const DetailCard: React.FC<DetailCardProps> = ({ result }) => {
  const {
    _type,
    title,
    shortTitle,
    abstract,
    blurb,
    content,
    externalUrl,
    _createdAt,
    publicationDate,
    date,
  } = result;

  const publishDate = publicationDate || date || _createdAt;
  const internalLink = InternalLinkBuilder(result);
  const displayTitle = shortTitle || title;

  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    externalUrl ? (
      <a
        href={externalUrl}
        target='_blank'
        rel='noopener noreferrer'
        aria-label={`Read the ${_type
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toLowerCase()} for ${displayTitle}`}
        className='relative block rounded-xl border-[2.5px] border-zinc-200 bg-white p-5 pb-16 transition-transform duration-300 ease-in-out hover:scale-[1.03] hover:shadow-md'
      >
        {children}
      </a>
    ) : (
      <Link
        internalLink={internalLink}
        ariaLabel={`Read the ${_type
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toLowerCase()} for ${displayTitle}`}
        className='relative block rounded-xl border-[2.5px] border-zinc-200 bg-white p-5 pb-16 transition-transform duration-300 ease-in-out hover:scale-[1.03] hover:shadow-md'
      >
        {children}
      </Link>
    );

  return (
    <Wrapper>
      <p className='mb-1 text-sm font-normal leading-5 text-zinc-800'>
        {_type
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toLowerCase()}
      </p>

      <h3 className='mb-2 line-clamp-2 text-2xl font-semibold leading-7 text-black'>
        {displayTitle}
      </h3>

      {_type === 'mediaCoverage' && abstract && (
        <p className='mb-4 line-clamp-2 text-base font-light leading-relaxed text-black'>
          {abstract}
        </p>
      )}
      {(_type === 'exhibit' || _type === 'event') && blurb && (
        <RichText
          value={blurb}
          className='mb-4 line-clamp-2 !text-base font-light text-black'
        />
      )}
      {_type === 'pressRelease' && content && (
        <RichText
          value={content}
          className='mb-4 line-clamp-2 !text-base font-light text-black'
        />
      )}

      <div className='absolute bottom-5 left-5 right-5 flex items-center justify-between border-t border-black border-opacity-15 pt-2'>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-1 text-sm leading-5 text-black'>
            <img
              src='/images/calender-icon.svg'
              className='mr-1'
              alt='Calendar Icon'
            />
            {publishDate && formattedDate(publishDate)}
          </div>
        </div>
        <div
          className='ring-offset-background group-focus:ring-ring icon-hover group relative inline-flex h-8 w-8 items-center justify-center whitespace-nowrap rounded-full p-0 font-semibold text-black transition-colors hover:text-white focus:text-white active:text-white disabled:cursor-not-allowed disabled:opacity-50 group-focus:outline-none group-focus:ring-2 group-focus:ring-offset-2'
          aria-hidden='true'
          role='presentation'
        >
          <div className='ease-bounce absolute inset-0 flex items-center overflow-hidden rounded-full bg-yellow-500 transition-all after:absolute after:block after:w-full after:scale-0 after:rounded-full after:bg-blue-950 after:pt-[100%] after:opacity-20 after:transition-all group-hover:scale-[1.05] group-hover:after:scale-100 group-hover:after:opacity-100 group-focus:scale-[1.05] group-focus:after:scale-100 group-focus:after:opacity-100 group-active:scale-[1.05] group-active:after:scale-100 group-active:after:opacity-100 md:group-hover:scale-[1.15] md:group-focus:scale-[1.15] md:group-active:scale-[1.15]'></div>
          <span className='relative'>
            <svg
              width='15'
              height='16'
              viewBox='0 0 15 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden='true'
            >
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M7.874 1.261c-.391-.391-1.024-.391-1.415 0s-.391 1.024 0 1.415L10.878 7.093H1.832c-.552 0-1 .448-1 1s.448 1 1 1h9.046L6.459 13.511c-.391.391-.391 1.024 0 1.415s1.024.391 1.415 0L13.695 9.105c.193-.193.304-.457.304-.735s-.111-.542-.304-.735L7.874 1.815V1.261z'
                fill='#000'
              />
            </svg>
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

export default DetailCard;
